import { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { formatDate } from '../dppApi';
import Pager from './Pager';
import { usePaged } from '../hooks/usePaged';
import '../App.css';

// The Estate plan editor — the single implementation used in TWO places with the
// identical UI: the owner's My Account page (own token) and the lawyer's scoped
// share link /estate/:token (token-authenticated, time-limited). The parent
// supplies `api` with the right endpoints and whether the "Share with lawyer"
// block shows; everything below is shared, so the two screens can't drift.
const EstatePlanner = ({
    api,
    showShare = false,
    title = 'Estate planning',
    subtitle = 'Decide who inherits your pieces. Private until exercised.',
    passportHref,   // (piece) => URL of that piece's passport (estate section); owner or token-scoped
}) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Executor editor.
    const [execName, setExecName] = useState('');
    const [execEmail, setExecEmail] = useState('');
    const [execBusy, setExecBusy] = useState(false);
    const [execSaved, setExecSaved] = useState(false);
    const [execEditing, setExecEditing] = useState(false);

    // Per-piece designation editors, keyed by product_id.
    const [edits, setEdits] = useState({});
    const [savingId, setSavingId] = useState(null);
    const [rowError, setRowError] = useState({});
    const [editingRows, setEditingRows] = useState({});

    // Will upload + share.
    const [willBusy, setWillBusy] = useState(false);
    const willInputRef = useRef(null);
    const [shareBusy, setShareBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    const [execShareBusy, setExecShareBusy] = useState(false);
    const [execCopied, setExecCopied] = useState(false);

    const seedEdits = (pieces) => {
        const next = {};
        pieces.forEach(p => {
            const d = p.designation || {};
            next[p.product_id] = {
                beneficiary_name: d.beneficiary_name || '',
                beneficiary_email: d.beneficiary_email || '',
                relationship: d.relationship || '',
            };
        });
        setEdits(next);
    };

    useEffect(() => {
        let alive = true;
        api.get()
            .then(d => {
                if (!alive) return;
                setData(d);
                setExecName(d.executor?.name || '');
                setExecEmail(d.executor?.email || '');
                setExecEditing(!(d.executor?.name || d.executor?.email));
                seedEdits(d.pieces || []);
            })
            .catch(err => { if (alive) setError(err.message); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveExecutor = async () => {
        setExecBusy(true);
        setExecSaved(false);
        try {
            const res = await api.saveExecutor({ name: execName, email: execEmail });
            setData(prev => ({ ...prev, executor: res.executor }));
            setExecSaved(true);
            if (res.executor?.name || res.executor?.email) setExecEditing(false);
        } catch (err) {
            setError(err.message);
        } finally {
            setExecBusy(false);
        }
    };

    const setEdit = (pid, field, value) =>
        setEdits(m => ({ ...m, [pid]: { ...m[pid], [field]: value } }));

    const saveDesignation = async (pid) => {
        const e = edits[pid] || {};
        setSavingId(pid);
        setRowError(m => ({ ...m, [pid]: null }));
        try {
            const res = await api.saveDesignation({
                product_id: pid,
                beneficiary_name: e.beneficiary_name,
                beneficiary_email: e.beneficiary_email,
                relationship: e.relationship,
            });
            setData(prev => ({
                ...prev,
                pieces: prev.pieces.map(p => p.product_id === pid ? { ...p, designation: res.designation } : p),
            }));
            setEditingRows(m => ({ ...m, [pid]: false }));
        } catch (err) {
            setRowError(m => ({ ...m, [pid]: err.message }));
        } finally {
            setSavingId(null);
        }
    };

    const startEditRow = (pid) => setEditingRows(m => ({ ...m, [pid]: true }));

    const cancelEditRow = (pid) => {
        const dsg = data?.pieces.find(p => p.product_id === pid)?.designation || {};
        setEdits(m => ({ ...m, [pid]: {
            beneficiary_name: dsg.beneficiary_name || '',
            beneficiary_email: dsg.beneficiary_email || '',
            relationship: dsg.relationship || '',
        } }));
        setRowError(m => ({ ...m, [pid]: null }));
        setEditingRows(m => ({ ...m, [pid]: false }));
    };

    const uploadWill = async (file) => {
        if (!file) return;
        setWillBusy(true);
        try {
            const form = new FormData();
            form.append('document', file);
            const res = await api.uploadWill(form);
            setData(prev => ({ ...prev, will: res.will }));
        } catch (err) {
            setError(err.message);
        } finally {
            setWillBusy(false);
            if (willInputRef.current) willInputRef.current.value = '';
        }
    };

    const createShare = async () => {
        setShareBusy(true);
        try {
            const grant = await api.createShare();
            setData(prev => ({ ...prev, grant }));
        } catch (err) {
            setError(err.message);
        } finally {
            setShareBusy(false);
        }
    };

    const revokeShare = async () => {
        setShareBusy(true);
        try {
            await api.revokeShare();
            setData(prev => ({ ...prev, grant: null }));
        } catch (err) {
            setError(err.message);
        } finally {
            setShareBusy(false);
        }
    };

    const copyRef = () => {
        const g = data?.grant;
        if (!g) return;
        navigator.clipboard?.writeText(g.share_url || g.reference_code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        }).catch(() => {});
    };

    const createExecutorShare = async () => {
        setExecShareBusy(true);
        try {
            const grant = await api.createExecutorShare();
            setData(prev => ({ ...prev, executor_grant: grant }));
        } catch (err) {
            setError(err.message);
        } finally {
            setExecShareBusy(false);
        }
    };

    const revokeExecutorShare = async () => {
        setExecShareBusy(true);
        try {
            await api.revokeExecutorShare();
            setData(prev => ({ ...prev, executor_grant: null }));
        } catch (err) {
            setError(err.message);
        } finally {
            setExecShareBusy(false);
        }
    };

    const copyExecRef = () => {
        const g = data?.executor_grant;
        if (!g) return;
        navigator.clipboard?.writeText(g.share_url || g.reference_code).then(() => {
            setExecCopied(true);
            setTimeout(() => setExecCopied(false), 1500);
        }).catch(() => {});
    };

    const pieces = data?.pieces || [];
    const designatedCount = pieces.filter(p => p.designation).length;
    const will = data?.will;
    const grant = data?.grant;
    const executorGrant = data?.executor_grant;
    const executorEmail = data?.executor?.email;
    const piecesPage = usePaged(pieces, { narrow: 8, wide: 8, resetKey: pieces.length });

    if (loading) return <p className="collection-status collection-status--flush">Loading…</p>;
    if (error && !data) {
        return <p className="dpp-error">{error}</p>;
    }

    return (
        <div className="settings-section">
            <div className="settings-section-head">
                <div>
                    {title && <h2 className="settings-section-title">{title}</h2>}
                    <p className="settings-section-sub">{subtitle}</p>
                    {data?.owner_name && (
                        <p className="settings-section-sub">
                            Estate of {data.owner_name}
                            {data.expires_at ? ` · your access expires ${formatDate(data.expires_at)}` : ''}
                        </p>
                    )}
                </div>
            </div>

            {error && <p className="dpp-error">{error}</p>}

            {/* Summary + will + executor — clean label/value rows, per the mock. */}
            <div className="estate-rows">
                <div className="estate-row">
                    <span className="estate-row-label">Pieces designated</span>
                    <span className="estate-row-value">{designatedCount} of {pieces.length}</span>
                </div>

                <div className="estate-row">
                    <span className="estate-row-label">Will document</span>
                    <span className="estate-row-value">
                        {will?.present ? (
                            will.url ? (
                                <a className="estate-doc-chip estate-doc-chip--link" href={will.url}
                                    target="_blank" rel="noopener noreferrer">
                                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 2h8l4 4v16H6zM14 2v4h4" /></svg>
                                    {will.filename || 'Will document'}
                                </a>
                            ) : (
                                <span className="estate-doc-chip">
                                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 2h8l4 4v16H6zM14 2v4h4" /></svg>
                                    {will.filename || 'Will attached'}
                                </span>
                            )
                        ) : (
                            <span className="estate-muted">Not attached</span>
                        )}
                        <button className="estate-inline-btn" disabled={willBusy}
                            onClick={() => willInputRef.current?.click()}>
                            {willBusy ? 'Uploading…' : (will?.present ? 'Replace' : 'Attach will')}
                        </button>
                        <input ref={willInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            style={{ display: 'none' }}
                            onChange={e => uploadWill(e.target.files[0] || null)} />
                    </span>
                </div>

                <div className="estate-row estate-row--exec">
                    <span className="estate-row-label">Executor</span>
                    {execEditing ? (
                        <span className="estate-row-value estate-exec-value">
                            <input className="dpp-input estate-input-sm" placeholder="Name" value={execName}
                                onChange={e => { setExecName(e.target.value); setExecSaved(false); }} />
                            <input className="dpp-input estate-input-sm" type="email" placeholder="Email" value={execEmail}
                                onChange={e => { setExecEmail(e.target.value); setExecSaved(false); }} />
                            <button className="estate-inline-btn" onClick={saveExecutor} disabled={execBusy}>
                                {execBusy ? 'Saving…' : 'Save'}
                            </button>
                            {execSaved && <span className="estate-saved">Saved</span>}
                        </span>
                    ) : (
                        <span className="estate-row-value estate-exec-value estate-exec-display">
                            <span className="estate-exec-readout">
                                <span className="estate-exec-name">{data.executor?.name || '—'}</span>
                                {data.executor?.email && <span className="estate-exec-email">{data.executor.email}</span>}
                            </span>
                            <button className="estate-inline-btn" onClick={() => { setExecEditing(true); setExecSaved(false); }}>Edit</button>
                        </span>
                    )}
                </div>
            </div>

            {/* Give executor access — a non-expiring, revocable link the executor uses LATER to
                upload the death certificate and exercise the will. Sits directly under the
                executor details; owner-side only. */}
            {showShare && api.createExecutorShare && (
                <div className="estate-share">
                    <div className="estate-share-head">
                        <div>
                            <h3 className="estate-block-title">Grant executor access</h3>
                            <p className="estate-block-sub">
                                A private, non-expiring link your executor uses to exercise your will.{' '}
                                {executorEmail ? `Emailed to ${executorEmail}.` : 'Add an executor email above to send it automatically.'} Revoke anytime.
                            </p>
                        </div>
                        {!executorGrant && (
                            <button className="catalogue-btn-primary" onClick={createExecutorShare} disabled={execShareBusy}>
                                {execShareBusy ? 'Creating…' : 'Grant executor access'}
                            </button>
                        )}
                    </div>

                    {executorGrant && (
                        <div className="estate-share-card">
                            <QRCodeSVG className="estate-share-qr" value={executorGrant.share_url} size={64} marginSize={2} />
                            <div className="estate-share-detail">
                                <span className="estate-share-reflabel">Reference code</span>
                                <span className="estate-share-ref">{executorGrant.reference_code}</span>
                                <div className="estate-share-actions">
                                    <button className="estate-inline-btn" onClick={copyExecRef}>{execCopied ? 'Copied' : 'Copy'}</button>
                                    <button className="estate-inline-btn estate-inline-btn--danger" onClick={revokeExecutorShare} disabled={execShareBusy}>Revoke</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Share with lawyer / executor — owner-side only. Sits above the designations table. */}
            {showShare && api.createShare && (
                <div className="estate-share">
                    <div className="estate-share-head">
                        <div>
                            <h3 className="estate-block-title">Share with lawyer</h3>
                            <p className="estate-block-sub">A scoped, revocable link to help set up your plan — expires automatically.</p>
                        </div>
                        {!grant && (
                            <button className="catalogue-btn-primary" onClick={createShare} disabled={shareBusy}>
                                {shareBusy ? 'Creating…' : 'Share with lawyer'}
                            </button>
                        )}
                    </div>

                    {grant && (
                        <div className="estate-share-card">
                            <QRCodeSVG className="estate-share-qr" value={grant.share_url} size={64} marginSize={2} />
                            <div className="estate-share-detail">
                                <span className="estate-share-reflabel">Reference code</span>
                                <span className="estate-share-ref">{grant.reference_code}</span>
                                {grant.expires_at && (
                                    <span className="estate-share-expiry">Expires {formatDate(grant.expires_at)}</span>
                                )}
                                <div className="estate-share-actions">
                                    <button className="estate-inline-btn" onClick={copyRef}>{copied ? 'Copied' : 'Copy'}</button>
                                    <button className="estate-inline-btn estate-inline-btn--danger" onClick={revokeShare} disabled={shareBusy}>Revoke</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* One compact table: each owned piece → designated owner + relationship. */}
            <h3 className="estate-table-title">Who inherits each piece</h3>
            {pieces.length === 0 ? (
                <p className="collection-status collection-status--flush">No pieces in this estate.</p>
            ) : (
                <>
                <table className="estate-table">
                    <thead>
                        <tr>
                            <th className="estate-col-piece">Piece</th>
                            <th className="estate-col-owner">Designated owner</th>
                            <th className="estate-col-rel">Relationship</th>
                            <th className="estate-col-act" aria-label="Actions"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {piecesPage.items.map(p => {
                            const e = edits[p.product_id] || {};
                            const busy = savingId === p.product_id;
                            const d = p.designation;
                            const editing = !d || editingRows[p.product_id];
                            return (
                                <tr key={p.product_id}>
                                    <td className="estate-col-piece">
                                        <div className="estate-td-piece">
                                            {p.image_url && p.image_url.startsWith('http')
                                                ? <img className="estate-td-thumb" src={p.image_url} alt={p.name} />
                                                : <span className="estate-td-thumb estate-td-thumb--empty" aria-hidden="true" />}
                                            <div className="estate-td-meta">
                                                <span className="estate-td-name">{p.name}</span>
                                                {p.designation && passportHref && p.qr_code_id && (
                                                    <a className="estate-view-passport" href={passportHref(p)}
                                                        target="_blank" rel="noopener noreferrer">View passport</a>
                                                )}
                                            </div>
                                        </div>
                                        {rowError[p.product_id] && <div className="dpp-error estate-td-err">{rowError[p.product_id]}</div>}
                                    </td>
                                    {editing ? (
                                        <>
                                            <td className="estate-col-owner">
                                                <input className="dpp-input estate-input-sm estate-td-input" placeholder="Name"
                                                    value={e.beneficiary_name || ''}
                                                    onChange={ev => setEdit(p.product_id, 'beneficiary_name', ev.target.value)} />
                                                <input className="dpp-input estate-input-sm estate-td-input" type="email" placeholder="Email"
                                                    value={e.beneficiary_email || ''}
                                                    onChange={ev => setEdit(p.product_id, 'beneficiary_email', ev.target.value)} />
                                            </td>
                                            <td className="estate-col-rel">
                                                <input className="dpp-input estate-input-sm estate-td-input" placeholder="Daughter"
                                                    value={e.relationship || ''}
                                                    onChange={ev => setEdit(p.product_id, 'relationship', ev.target.value)} />
                                            </td>
                                            <td className="estate-col-act">
                                                <div className="estate-td-actions">
                                                    <button className="estate-btn estate-btn--primary" disabled={busy}
                                                        onClick={() => saveDesignation(p.product_id)}>
                                                        {busy ? '…' : 'Save'}
                                                    </button>
                                                    {d && (
                                                        <button className="estate-btn estate-btn--secondary" disabled={busy}
                                                            onClick={() => cancelEditRow(p.product_id)}>Cancel</button>
                                                    )}
                                                </div>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td className="estate-col-owner">
                                                <div className="estate-designee">
                                                    <span className="estate-designee-name">{d.beneficiary_name || '—'}</span>
                                                    {d.beneficiary_email && <span className="estate-designee-email">{d.beneficiary_email}</span>}
                                                </div>
                                            </td>
                                            <td className="estate-col-rel">
                                                <span className="estate-designee-rel">{d.relationship || '—'}</span>
                                            </td>
                                            <td className="estate-col-act">
                                                <div className="estate-td-actions">
                                                    <button className="estate-btn estate-btn--secondary" onClick={() => startEditRow(p.product_id)}>Edit</button>
                                                </div>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {/* Mobile My Account only: the table is hidden below 640px and this
                    card list shows instead (see .account-settings .estate-cards in
                    App.css). Same edit state/handlers as the table. */}
                <div className="estate-cards">
                    {piecesPage.items.map(p => {
                        const e = edits[p.product_id] || {};
                        const busy = savingId === p.product_id;
                        const d = p.designation;
                        const editing = !!editingRows[p.product_id];
                        return (
                            <div className="estate-card" key={`card-${p.product_id}`}>
                                <div className="estate-card-row">
                                    {p.image_url && p.image_url.startsWith('http')
                                        ? <img className="estate-card-thumb" src={p.image_url} alt={p.name} />
                                        : <span className="estate-card-thumb estate-card-thumb--empty" aria-hidden="true" />}
                                    <div className="estate-card-meta">
                                        <span className="estate-card-name">{p.name}</span>
                                        {!editing && (
                                            <span className="estate-card-desig">
                                                {d
                                                    ? <>{d.beneficiary_name || '—'}{d.relationship && <span className="estate-muted"> · {d.relationship}</span>}</>
                                                    : <span className="estate-muted">Not designated</span>}
                                            </span>
                                        )}
                                    </div>
                                    {!editing && (
                                        <button className="estate-inline-btn" onClick={() => startEditRow(p.product_id)}>
                                            {d ? 'Edit' : 'Assign'}
                                        </button>
                                    )}
                                </div>
                                {editing && (
                                    <div className="estate-card-edit">
                                        <input className="dpp-input estate-input-sm" placeholder="Name"
                                            value={e.beneficiary_name || ''}
                                            onChange={ev => setEdit(p.product_id, 'beneficiary_name', ev.target.value)} />
                                        <input className="dpp-input estate-input-sm" type="email" placeholder="Email"
                                            value={e.beneficiary_email || ''}
                                            onChange={ev => setEdit(p.product_id, 'beneficiary_email', ev.target.value)} />
                                        <input className="dpp-input estate-input-sm" placeholder="Relationship"
                                            value={e.relationship || ''}
                                            onChange={ev => setEdit(p.product_id, 'relationship', ev.target.value)} />
                                        {rowError[p.product_id] && <div className="dpp-error">{rowError[p.product_id]}</div>}
                                        <div className="estate-card-actions">
                                            <button className="estate-btn estate-btn--primary" disabled={busy}
                                                onClick={() => saveDesignation(p.product_id)}>{busy ? '…' : 'Save'}</button>
                                            <button className="estate-btn estate-btn--secondary" disabled={busy}
                                                onClick={() => cancelEditRow(p.product_id)}>Cancel</button>
                                        </div>
                                    </div>
                                )}
                                {d && passportHref && p.qr_code_id && !editing && (
                                    <a className="estate-view-passport" href={passportHref(p)}
                                        target="_blank" rel="noopener noreferrer">View passport</a>
                                )}
                            </div>
                        );
                    })}
                </div>
                </>
            )}
            {pieces.length > 0 && (
                <Pager
                    page={piecesPage.page}
                    pageCount={piecesPage.pageCount}
                    onPage={piecesPage.setPage}
                    total={piecesPage.total}
                    pageSize={piecesPage.pageSize}
                    label="pieces"
                />
            )}
        </div>
    );
};

export default EstatePlanner;
